// Utility functions for handling filter display logic across components

/**
 * Interface for data source items from localStorage
 */
export interface DataSource {
    id: string;
    name?: string;
    title?: string;
    product_line_filter: string | string[];
}

/**
 * Interface for chat preferences structure
 */
export interface ChatPreferences {
    selectedProductLines?: string[];
    selectedDatasources?: string[];
}

/**
 * Get data source names from IDs with cleaned formatting
 */
function getDataSourceNames(sourceIds: string[], userDatasources: DataSource[]): string[] {
    const dataSourceMap: Record<string, string> = {};
    for (const ds of userDatasources) {
        dataSourceMap[ds.id] = ds.name || ds.title || ds.id;
    }

    return sourceIds
        .map((sourceId: string) => dataSourceMap[sourceId] || sourceId)
        .map((name: string) =>
            name.replace(" Database", "").replace(" Cases", "").replace(" Reports", ""),
        );
}

/**
 * Get all data source IDs that belong to a specific product line
 */
function getProductLineDataSources(productLine: string, userDatasources: DataSource[]): string[] {
    return userDatasources
        .filter((ds) => {
            return (
                ds.product_line_filter === productLine ||
                (Array.isArray(ds.product_line_filter) &&
                    ds.product_line_filter.includes(productLine))
            );
        })
        .map((ds) => ds.id);
}

/**
 * Generate display text for filters based on selected product lines and data sources
 * @param chatPreferences The user's selected preferences
 * @param isForTitle Whether the text is for a title (true) or button label (false)
 * @returns The formatted display text
 */
export function getFilterDisplayText(chatPreferences: ChatPreferences, isForTitle = false): string {
    try {
        let productLine = chatPreferences.selectedProductLines?.[0];
        if (!productLine || productLine === "all") {
            productLine = "Default Filters";
        }

        const selectedSources = chatPreferences.selectedDatasources || [];

        // Get user datasources from localStorage
        const userDatasources = JSON.parse(
            localStorage.getItem("datasources") || "[]",
        ) as DataSource[];

        const sourceNames = getDataSourceNames(selectedSources, userDatasources);
        const baseText = isForTitle ? "CTS Knowledge Search" : "Preferences";

        // If only one datasource is selected, show just that datasource
        if (selectedSources.length === 1) {
            return `${baseText}${isForTitle ? " - " : ": "}${sourceNames[0]}`;
        }

        // Default filters case
        if (productLine === "Default Filters") {
            return isForTitle ? baseText : `${baseText}: Default Filters`;
        }

        // Check if all sources for this product line are selected
        const allProductLineSources = getProductLineDataSources(productLine, userDatasources);
        const hasAllSourcesForProductLine =
            allProductLineSources.length > 0 &&
            allProductLineSources.every((id: string) => selectedSources.includes(id));

        // If no sources selected or all sources for product line selected, show just product line
        if (selectedSources.length === 0 || hasAllSourcesForProductLine) {
            return `${baseText}${isForTitle ? " - " : ": "}${productLine}`;
        }

        // Multiple sources selected (but not all for the product line)
        if (sourceNames.length <= 3) {
            return `${baseText}${isForTitle ? " - " : ": "}${productLine} (${sourceNames.join(", ")})`;
        }

        return `${baseText}${isForTitle ? " - " : ": "}${productLine} (${sourceNames.length} sources)`;
    } catch (error) {
        console.error("Error generating filter display text:", error);
        return isForTitle ? "CTS Knowledge Search" : "Preferences";
    }
}
